import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid2';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { isOptionEqualToValue } from 'feature/dispatching/utils';
import {
  useGetDestinationsQuery,
  useGetLoadingControlsBySupplierQuery,
  useGetRecipesBySupplierQuery,
  useGetSuppliersQuery,
} from '../../loadsEndpoints';
import LoadingTextField from './LoadingTextField';

const accordionStyles = {
  boxShadow: 'none',
  fontSize: '1.25em',
  m: 2,
};
const accordionDetailsStyles = {
  backgroundColor: 'surface.white',
};

const CustomRecipeForm = ({ loadState, setLoadState, compartment }) => {
  const { location } = loadState;
  const { compartmentId, destination, loadingControl, recipe, supplier } = compartment;

  const [destinationAutocomplete, setDestinationAutocomplete] = useState('name');
  const compartmentLens = R.lensPath(['compartments', compartmentId]);
  const supplierLens = R.compose(compartmentLens, R.lensProp('supplier'));
  const loadingControlLens = R.compose(compartmentLens, R.lensProp('loadingControl'));
  const recipeLens = R.compose(compartmentLens, R.lensProp('recipe'));
  const destinationLens = R.compose(compartmentLens, R.lensProp('destination'));
  const volumeLens = R.compose(compartmentLens, R.lensProp('volume'));

  const {
    data: suppliersQuery = [],
    isFetching: isFetchingSuppliers,
    isSuccess: isSuppliersSuccess,
  } = useGetSuppliersQuery({ locationId: R.prop('locationId', location) });

  const {
    data: loadingControlsQuery = [],
    isFetching: isFetchingLoadingControls,
    isSuccess: isLoadingControlsSuccess,
  } = useGetLoadingControlsBySupplierQuery(
    { locationId: R.prop('locationId', location), supplierId: R.prop('supplierId', supplier) },
    {
      skip: R.isNil(supplier),
    }
  );

  const {
    data: recipesQuery = [],
    isFetching: isFetchingRecipes,
    isSuccess: isRecipesSuccess,
  } = useGetRecipesBySupplierQuery(
    {
      locationId: R.prop('locationId', location),
      supplierId: R.prop('supplierId', supplier),
    },
    { skip: R.isNil(supplier) }
  );

  const {
    data: destinationsQuery = [],
    isFetching: isFetchingDestinations,
    isSuccess: isDestinationsSuccess,
  } = useGetDestinationsQuery(
    {
      loadingControlIds: R.o(R.of, R.prop('loadingControlId'))(loadingControl),
      locationId: R.prop('locationId')(location),
    },
    {
      skip: [R.isNil(supplier)].includes(true),
    }
  );

  const handleSupplierChange = (_, value) => {
    const updateSupplier = R.compose(
      R.set(loadingControlLens, null),
      R.set(recipeLens, null),
      R.set(supplierLens, value),
      R.set(volumeLens, ''),
      R.set(destinationLens, null)
    );
    setLoadState(updateSupplier);
  };

  const handleLoadingControlChange = (_, value) => {
    // prettier-ignore
    const updateLcn = R.compose(
      R.set(destinationLens, null),
      R.set(loadingControlLens, value)
    );
    setLoadState(updateLcn);
  };

  const updateRecipe = (value, init = false) => {
    // prettier-ignore
    let updatedRecipe;
    if (init) {
      updatedRecipe = R.compose(R.set(recipeLens, value));
    } else {
      updatedRecipe = R.compose(R.set(recipeLens, value), R.set(volumeLens, ''));
    }
    setLoadState(updatedRecipe);
  };

  const handleRecipeChange = (_, value) => {
    updateRecipe(value);
  };

  const handleDestinationChange = (_, value) => {
    setLoadState(R.set(destinationLens, value));
  };

  useEffect(() => {
    if (!recipe) return;

    if (isRecipesSuccess) {
      const thisRecipe = recipesQuery.find(
        r => r.recipeId === recipe.recipeId || r.productGradeCode === recipe.recipeId
      );
      if (thisRecipe) {
        updateRecipe(thisRecipe, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecipesSuccess, recipe]);

  const handleToggle = (e, selected) => {
    setDestinationAutocomplete(selected);
  };

  return (
    <Stack spacing={2}>
      <Autocomplete
        disableClearable
        disabled={!isSuppliersSuccess}
        getOptionLabel={R.o(R.toString, R.prop('supplierId'))}
        isOptionEqualToValue={isOptionEqualToValue('supplierId')}
        loading={isFetchingSuppliers}
        onChange={handleSupplierChange}
        options={suppliersQuery}
        renderInput={params => (
          <LoadingTextField label="Supplier" loading={isFetchingSuppliers} params={params} />
        )}
        value={supplier}
      />

      {compartment.supplier?.hasLoadingControls && (
        <Autocomplete
          disabled={!isLoadingControlsSuccess}
          getOptionLabel={R.prop('loadingControlNumber')}
          isOptionEqualToValue={isOptionEqualToValue('loadingControlId')}
          loading={isFetchingLoadingControls}
          onChange={handleLoadingControlChange}
          options={loadingControlsQuery}
          renderInput={params => (
            <LoadingTextField
              label="LCN/PETROEX"
              loading={isFetchingLoadingControls}
              params={params}
            />
          )}
          value={loadingControl}
        />
      )}

      <Autocomplete
        disableClearable
        disabled={!isRecipesSuccess}
        getOptionLabel={r => `${R.prop('recipeId', r)}: ${R.prop('name', r)}`}
        isOptionEqualToValue={isOptionEqualToValue('recipeId')}
        loading={isFetchingRecipes}
        onChange={handleRecipeChange}
        options={recipesQuery}
        renderInput={params => (
          <LoadingTextField label="Recipe" loading={isFetchingRecipes} params={params} />
        )}
        value={recipe}
      />
      <Typography label="Destination Information" sx={{ mt: 2, mx: 2 }}>
        Destination Information
      </Typography>
      <ToggleButtonGroup exclusive onChange={handleToggle} value={destinationAutocomplete}>
        <ToggleButton value="name">Name</ToggleButton>
        <ToggleButton value="address">Address</ToggleButton>
      </ToggleButtonGroup>
      {destinationAutocomplete === 'address' ? (
        <Autocomplete
          disableClearable
          disabled={!isDestinationsSuccess}
          getOptionLabel={({ city, state, street, zipCode }) =>
            `${street} ${city}, ${state} ${zipCode} `
          }
          isOptionEqualToValue={isOptionEqualToValue('destinationId')}
          loading={isFetchingDestinations}
          onChange={handleDestinationChange}
          options={destinationsQuery}
          renderInput={params => (
            <LoadingTextField label="ADDRESS" loading={isFetchingDestinations} params={params} />
          )}
          value={destination}
        />
      ) : (
        <Autocomplete
          disableClearable
          disabled={!isDestinationsSuccess}
          getOptionLabel={({ destinationId, name }) => `${destinationId}: ${name}`}
          isOptionEqualToValue={isOptionEqualToValue('destinationId')}
          loading={isFetchingDestinations}
          onChange={handleDestinationChange}
          options={destinationsQuery}
          renderInput={params => (
            <LoadingTextField label="NAME" loading={isFetchingDestinations} params={params} />
          )}
          value={destination}
        />
      )}

      <Accordion sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} label="Destination" sx={{ p: 1 }}>
          Destination
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          <Grid columnSpacing={{ md: 3, sm: 2, xs: 1 }} container rowSpacing={1}>
            <Grid gridColumn="span 4" size={{ xs: 6 }}>
              <Typography gutterBottom item variant="caption">
                Destination ID{' '}
              </Typography>
              <Typography sx={{ mb: 2 }} variant="body1bold">
                {destination ? destination?.destinationId : null}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography gutterBottom item variant="caption">
                Destination Name{' '}
              </Typography>
              <Typography item sx={{ mb: 2 }} variant="body1bold">
                {destination ? destination?.name : null}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography gutterBottom variant="caption">
                Address{' '}
              </Typography>
              <Typography sx={{ mb: 2 }} variant="body1bold">
                {destination
                  ? `${destination?.street} ${destination?.city}  ${destination?.state}  
                    ${destination?.zipCode ? destination.zipCode : ''}`
                  : null}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

CustomRecipeForm.propTypes = {
  compartment: PropTypes.shape({
    compartmentId: PropTypes.number,
    destination: PropTypes.arrayOf(PropTypes.shape({})),
    loadType: PropTypes.shape({}),
    loadingControl: PropTypes.shape({}),
    product: PropTypes.shape({}),
    products: PropTypes.shape({}),
    recipe: PropTypes.shape({ recipeId: PropTypes.string }),
    supplier: PropTypes.shape({
      hasLoadingControls: PropTypes.bool,
    }),
  }).isRequired,
  loadState: PropTypes.shape({
    compartments: PropTypes.shape({}),
    location: PropTypes.shape({}),
  }).isRequired,
  setLoadState: PropTypes.func.isRequired,
};

export default CustomRecipeForm;
